import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiEndpoints } from '../services/api';
import { setToken } from '../utils/auth';
import { useAppContext } from '../contexts/AppContext';

const accent = '#059669';

const DriverLogin = () => {
  const navigate       = useNavigate();
  const { login }      = useAppContext();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await apiEndpoints.auth.driverLogin({ username, password });
      setToken(res.data.token);
      login(res.data.token);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.wrapper}>
        {/* banner */}
        <img src="/logo-login.png" alt="Fleet OS" style={{ width: '100%', height: 120, objectFit: 'contain', display: 'block', marginBottom: 20 }} />

        {/* card */}
        <div style={s.card}>
          <div style={{ ...s.cardStripe, background: accent }} />
          <div style={s.cardBody}>
            <div style={s.cardHead}>
              <div style={{ ...s.badge, background: accent + '22', border: `1.5px solid ${accent}44` }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                </svg>
              </div>
              <div>
                <div style={s.cardTitle}>Driver Login</div>
                <div style={s.cardSub}>View fleet &amp; report incidents</div>
              </div>
            </div>

            {error && (
              <div style={s.errorBox}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="#dc2626" style={{ flexShrink: 0, marginTop: 1 }}>
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 11 }}>
                <label style={s.label}>Username</label>
                <input
                  style={s.input}
                  type="text"
                  placeholder="your.username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  required
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  onFocus={e => Object.assign(e.target.style, { borderColor: accent, background: '#ffffff', boxShadow: `0 0 0 3px ${accent}1e` })}
                  onBlur={e => Object.assign(e.target.style, { borderColor: '#e5e7eb', background: '#f9fafb', boxShadow: 'none' })}
                />
              </div>
              <div style={{ marginBottom: 11 }}>
                <label style={s.label}>Password</label>
                <input
                  style={s.input}
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  onFocus={e => Object.assign(e.target.style, { borderColor: accent, background: '#ffffff', boxShadow: `0 0 0 3px ${accent}1e` })}
                  onBlur={e => Object.assign(e.target.style, { borderColor: '#e5e7eb', background: '#f9fafb', boxShadow: 'none' })}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{ ...s.submitBtn, background: loading ? '#94a3b8' : accent, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                    <span style={s.spinner} /> Signing in…
                  </span>
                ) : 'Sign in'}
              </button>
            </form>

            <div style={{ ...s.hintBox, borderColor: accent + '33', background: accent + '0d', marginTop: 12 }}>
              <svg width="13" height="13" viewBox="0 0 20 20" fill={accent} style={{ flexShrink: 0, marginTop: 1 }}>
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.55 }}>
                Your username and password are assigned by your fleet administrator.
              </span>
            </div>
          </div>
        </div>

        <div style={s.footer}>
          <svg width="12" height="12" viewBox="0 0 20 20" fill="#9ca3af" style={{ flexShrink: 0 }}>
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          Secured with JWT · All sessions are encrypted
        </div>

        <div style={{ marginTop: 14, fontSize: 12, color: '#9ca3af' }}>
          Are you an admin?{' '}
          <Link to="/login" style={{ color: '#0077b6', fontWeight: 600, textDecoration: 'none' }}>
            Admin login →
          </Link>
        </div>
      </div>

      <style>{`@keyframes login-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f0f2f5',
    padding: '20px',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    overflowY: 'auto',
  },
  wrapper: {
    width: '100%',
    maxWidth: 420,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  brandIcon: {
    width: 42,
    height: 42,
    background: 'linear-gradient(135deg, #047857, #10b981)',
    borderRadius: 11,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 6px 20px rgba(5,150,105,0.4)',
  },
  brandTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: '#0d1117',
    letterSpacing: -0.5,
    lineHeight: 1.1,
  },
  brandSub: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: 500,
    marginTop: 2,
  },
  headline: {
    fontSize: 17,
    fontWeight: 700,
    color: '#0d1117',
    marginBottom: 4,
    letterSpacing: -0.3,
  },
  headlineSub: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 20,
    fontWeight: 400,
    textAlign: 'center',
  },
  card: {
    width: '100%',
    background: '#ffffff',
    borderRadius: 20,
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
  },
  cardStripe: {
    height: 4,
    flexShrink: 0,
  },
  cardBody: {
    padding: '22px 28px 26px',
  },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  badge: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: '#0d1117',
    letterSpacing: -0.3,
    lineHeight: 1.2,
  },
  cardSub: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: 500,
    marginTop: 2,
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: '#374151',
    marginBottom: 5,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    border: '1.5px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 13,
    color: '#0d1117',
    background: '#f9fafb',
    outline: 'none',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxSizing: 'border-box',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  submitBtn: {
    width: '100%',
    padding: '11px',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    marginTop: 4,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    transition: 'opacity 0.15s, transform 0.12s',
    boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
  },
  spinner: {
    display: 'inline-block',
    width: 14,
    height: 14,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'login-spin 0.7s linear infinite',
  },
  hintBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    border: '1px solid',
    borderRadius: 7,
    padding: '8px 10px',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    background: '#fff1f2',
    border: '1px solid #fecdd3',
    color: '#dc2626',
    borderRadius: 8,
    padding: '9px 13px',
    fontSize: 13,
    marginBottom: 16,
    lineHeight: 1.4,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 14,
    fontSize: 11,
    color: '#9ca3af',
    fontWeight: 500,
  },
};

export default DriverLogin;
